import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell,
} from "recharts";
import {
  Bus, ArrowLeft, ClipboardList, Coins, Timer, Users, AlertTriangle, Wrench,
  Calendar, Camera, FileText, Eye, TrendingUp,
} from "lucide-react";
import { Lightbox, useLightbox } from "@/components/Lightbox";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ExternalServiceDialog, ExternalServiceCard } from "@/components/ExternalServiceDialog";
import { canEditExternal } from "@/pages/ExternalServices";

export default function VehicleProfile() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const lightbox = useLightbox();
  const token = localStorage.getItem("access_token");

  useEffect(() => {
    api.get(`/vehicles/by-code/${encodeURIComponent(code)}/profile`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Yükleme hatası"));
  }, [code]);

  const kmData = useMemo(() => (data?.km_history || []).slice(-20), [data]);
  const faultData = useMemo(() => (data?.top_faults || []).slice(0, 6).map((f) => ({
    name: f.fault.slice(0, 22) + (f.fault.length > 22 ? "…" : ""),
    count: f.count,
    repeat: f.repeat_count,
  })), [data]);

  if (err) {
    return (
      <div className="text-center py-16">
        <div className="text-red-600 font-heading text-xl mb-2">{err}</div>
        <Link to="/araclar" className="text-[#1E3A8A] hover:underline">← Filo listesine dön</Link>
      </div>
    );
  }
  if (!data) return <div className="text-center py-16 text-slate-400">Yükleniyor…</div>;

  const v = data.vehicle;
  const s = data.stats;

  return (
    <div className="space-y-6" data-testid="vehicle-profile-page">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <Link to="/araclar" className="text-xs text-slate-500 hover:text-[#1E3A8A] font-mono uppercase tracking-widest inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Filo Listesi
          </Link>
          <div className="mt-1 text-xs uppercase tracking-[0.25em] text-slate-500 font-mono">Araç Sicil Kartı</div>
          <div className="mt-1 flex items-center gap-4 flex-wrap">
            <div className="w-14 h-14 rounded-lg bg-[#1E3A8A] flex items-center justify-center">
              <Bus className="w-8 h-8 text-white" />
            </div>
            <div>
              <div className="font-heading font-extrabold text-4xl tracking-tight text-slate-900">
                <span className="font-mono text-[#1E3A8A]">{v.code}</span>
                <span className="text-slate-300 mx-2">—</span>
                {v.plate}
                {v.brand && (<><span className="text-slate-300 mx-2">—</span><span data-testid="vehicle-profile-brand">{v.brand}</span></>)}
              </div>
              <BrandEditor vehicle={v} onSaved={(brand) => setData({ ...data, vehicle: { ...v, brand } })} />
              <div className="text-sm text-slate-500 mt-1">
                {v.route ? `Hat: ${v.route}` : "Hat bilgisi yok"}
                {v.model && <span> · Model: {v.model}</span>}
                {v.year && <span> · {v.year}</span>}
              </div>
            </div>
          </div>
        </div>
        {(v.next_service_date || v.next_inspection_date) && (
          <div className="flex gap-3">
            {v.next_service_date && (
              <DateBadge label="Servis" date={v.next_service_date} icon={<Wrench className="w-3.5 h-3.5" />} />
            )}
            {v.next_inspection_date && (
              <DateBadge label="Muayene" date={v.next_inspection_date} icon={<Calendar className="w-3.5 h-3.5" />} />
            )}
          </div>
        )}
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Kpi label="Toplam İş Emri" value={s.total_orders} icon={<ClipboardList className="w-4 h-4" />} />
        <Kpi label="Başarılı" value={s.success_count} icon={<Wrench className="w-4 h-4" />} color="emerald" />
        <Kpi label="Tekrar Arıza" value={s.repeat_count} icon={<AlertTriangle className="w-4 h-4" />} color="red" />
        <Kpi label="Başarı" value={`%${s.success_rate}`} icon={<TrendingUp className="w-4 h-4" />} />
        <Kpi label="Toplam Süre" value={`${Math.round(s.total_duration_minutes / 60)}s`} icon={<Timer className="w-4 h-4" />} />
        <Kpi label="Toplam Prim" value={`₺${s.total_tl.toLocaleString("tr-TR")}`} icon={<Coins className="w-4 h-4" />} accent />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200 shadow-none" data-testid="km-chart">
          <CardHeader className="border-b border-slate-100">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono">Kilometre Geçmişi</div>
            <CardTitle className="font-heading text-lg mt-1">Araç KM Grafiği</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {kmData.length === 0 ? (
              <div className="h-56 flex items-center justify-center text-slate-400 text-sm">KM verisi yok</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={kmData} margin={{ top: 5, right: 12, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748B" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} formatter={(v) => `${v.toLocaleString("tr-TR")} km`} />
                  <Line type="monotone" dataKey="km" stroke="#FF7A1A" strokeWidth={2.5} dot={{ r: 4, fill: "#FF7A1A" }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-none" data-testid="fault-chart">
          <CardHeader className="border-b border-slate-100">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono">Kronik Analiz</div>
            <CardTitle className="font-heading text-lg mt-1">En Sık Görülen Arızalar</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {faultData.length === 0 ? (
              <div className="h-56 flex items-center justify-center text-slate-400 text-sm">Arıza verisi yok</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={faultData} layout="vertical" margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: "#64748B" }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#64748B" }} width={140} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Bar dataKey="count" fill="#FF7A1A" radius={[0, 4, 4, 0]}>
                    {faultData.map((f, i) => (
                      <Cell key={i} fill={f.repeat > 0 ? "#FF5C5C" : "#FF7A1A"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Photos gallery */}
      {data.photos.length > 0 && (
        <Card className="border-slate-200 shadow-none">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono">Görsel Arşiv</div>
                <CardTitle className="font-heading text-lg mt-1">Fotoğraflar & Faturalar ({data.photos.length})</CardTitle>
              </div>
              <Camera className="w-4 h-4 text-slate-400" />
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2" data-testid="photos-gallery">
              {data.photos.map((p, i) => {
                const url = `${API}/files/${p.path}`;
                const isPdf = p.path.toLowerCase().endsWith(".pdf");
                return (
                  <button
                    type="button"
                    key={i}
                    onClick={() => isPdf ? window.open(url, "_blank") : lightbox.open(url)}
                    className="group relative aspect-square border border-slate-200 rounded overflow-hidden bg-slate-50 hover:border-[#1E3A8A]"
                    title={p.date}
                  >
                    {isPdf ? (
                      <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 text-[9px] font-mono">
                        <FileText className="w-6 h-6 mb-0.5" /> PDF
                      </div>
                    ) : (
                      <img src={url} alt={`ek-${i}`} className="w-full h-full object-cover" />
                    )}
                    <div className="absolute inset-0 bg-[#1E3A8A]/0 group-hover:bg-[#1E3A8A]/30 transition-colors flex items-center justify-center">
                      <Eye className="w-5 h-5 text-white opacity-0 group-hover:opacity-100" />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] font-mono text-center py-0.5 opacity-0 group-hover:opacity-100">
                      {p.date}
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Parts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200 shadow-none">
          <CardHeader className="border-b border-slate-100">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono">Parça Envanteri</div>
            <CardTitle className="font-heading text-lg mt-1">Değişen Tüm Parçalar</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="max-h-96 overflow-y-auto">
              {data.parts.length === 0 && <div className="text-center py-8 text-slate-400 text-sm">Parça kaydı yok</div>}
              {data.parts.map((p, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-b-0">
                  <div>
                    <div className="text-sm font-medium text-slate-800">{p.name}</div>
                    <div className="text-[10px] text-slate-500 font-mono">Son: {p.last_date || "-"}</div>
                  </div>
                  <Badge variant="outline" className="font-mono">×{p.count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-none">
          <CardHeader className="border-b border-slate-100">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono">Ustalar</div>
            <CardTitle className="font-heading text-lg mt-1">Bu Araca Dokunan Ustalar ({s.mechanics_touched})</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-2">
              {s.mechanics_list.map((m, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded text-sm">
                  <div className="w-6 h-6 rounded-full bg-[#1E3A8A] text-white text-xs flex items-center justify-center font-bold">
                    {m[0]}
                  </div>
                  {m}
                </div>
              ))}
              {s.mechanics_list.length === 0 && <div className="text-slate-400 text-sm">Kayıt yok</div>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dış servis */}
      <ExternalServicesSection vehicle={v} />

      {/* Full history */}
      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono">Tam Geçmiş</div>
          <CardTitle className="font-heading text-lg mt-1">İş Emri Kayıtları ({data.orders.length})</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider">Tarih</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Seviye</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Ana Usta</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Arıza</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Süre</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Durum</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.orders.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Bu araç için iş emri kaydı yok</TableCell></TableRow>
                )}
                {data.orders.map((o) => (
                  <TableRow key={o.id} className="hover:bg-slate-50">
                    <TableCell className="font-mono text-xs whitespace-nowrap">{o.date?.slice(0, 10)}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{(o.job_levels || []).join(", ") || o.job_level}</Badge></TableCell>
                    <TableCell>{o.primary_mechanic_name}</TableCell>
                    <TableCell className="max-w-xs truncate text-sm text-slate-700">{o.fault_reported}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{o.duration_minutes}dk</TableCell>
                    <TableCell>
                      {o.repair_status === "Başarılı Onarım"
                        ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Başarılı</Badge>
                        : <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Tekrar</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Lightbox src={lightbox.src} onClose={lightbox.close} />
    </div>
  );
}

function ExternalServicesSection({ vehicle }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [dlg, setDlg] = useState({ open: false, record: null });
  const load = () => api.get("/external-services", { params: { vehicle_id: vehicle.id } }).then((r) => setRows(r.data || [])).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [vehicle.id]);
  const canAdd = ["admin", "data_entry"].includes(user?.role);
  const del = async (r) => {
    if (!window.confirm("Bu dış servis kaydı silinsin mi?")) return;
    try { await api.delete(`/external-services/${r.id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };
  return (
    <Card className="border-slate-200 shadow-none" data-testid="vehicle-external-services">
      <CardHeader className="border-b border-slate-100">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono">Dış Servis</div>
            <CardTitle className="font-heading text-lg mt-1">Dış Servis Kayıtları ({rows.length})</CardTitle>
          </div>
          {canAdd && (
            <Button size="sm" variant="outline" className="border-orange-400 text-orange-700 hover:bg-orange-50" onClick={() => setDlg({ open: true, record: null })} data-testid="vehicle-es-add-btn">
              <Wrench className="w-4 h-4 mr-1" /> Dış Servis Kaydı
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        {rows.length === 0 && <div className="text-center py-6 text-slate-400 text-sm">Dış servis kaydı yok</div>}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {rows.map((r) => (
            <ExternalServiceCard key={r.id} r={r}
              onEdit={canEditExternal(user, r) ? (x) => setDlg({ open: true, record: x }) : null}
              onDelete={user?.role === "admin" ? del : null} />
          ))}
        </div>
      </CardContent>
      <ExternalServiceDialog open={dlg.open} record={dlg.record} defaultVehicleId={vehicle.id}
        onOpenChange={(o) => setDlg({ ...dlg, open: o })} onSaved={load} />
    </Card>
  );
}

function BrandEditor({ vehicle, onSaved }) {
  const { user } = useAuth();
  const [val, setVal] = useState(vehicle.brand || "");
  const [saving, setSaving] = useState(false);
  if (user?.role !== "admin") return null;
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/vehicles/${vehicle.id}`, { ...vehicle, brand: val.trim() });
      onSaved(r.data?.brand || "");
      toast.success("Marka güncellendi");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="mt-2 flex items-center gap-2" data-testid="brand-editor">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Marka</span>
      <Input value={val} onChange={(e) => setVal(e.target.value)} className="h-8 w-48 text-sm" placeholder="Örn: Mercedes" data-testid="vehicle-profile-brand-input" />
      <Button size="sm" onClick={save} disabled={saving || val.trim() === (vehicle.brand || "")} className="h-8 bg-[#1E3A8A] hover:bg-[#1e40af]" data-testid="vehicle-profile-brand-save">
        {saving ? "Kaydediliyor…" : "Kaydet"}
      </Button>
    </div>
  );
}

function Kpi({ label, value, icon, accent, color }) {
  const colorMap = {
    emerald: "text-emerald-600",
    red: "text-red-600",
  };
  return (
    <div className={`bg-white border border-slate-200 rounded-lg p-4 ${accent ? "border-l-4 border-l-[#1E3A8A]" : ""}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
        <div className="w-6 h-6 rounded flex items-center justify-center bg-blue-50 text-[#1E3A8A]">{icon}</div>
      </div>
      <div className={`metric-num text-2xl font-bold ${color ? colorMap[color] : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function DateBadge({ label, date, icon }) {
  const days = Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24));
  const color = days < 0 ? "red" : days <= 15 ? "amber" : days <= 30 ? "blue" : "slate";
  const bg = {
    red: "bg-red-50 border-red-200 text-red-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    blue: "bg-blue-50 border-blue-200 text-[#1E3A8A]",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  }[color];
  return (
    <div className={`border rounded-md px-4 py-2 ${bg}`}>
      <div className="text-[10px] uppercase font-mono tracking-widest flex items-center gap-1">{icon} {label}</div>
      <div className="text-sm font-heading font-bold mt-0.5">
        {new Date(date).toLocaleDateString("tr-TR")}
      </div>
      <div className="text-[10px] font-mono">
        {days < 0 ? `${-days} gün gecikti` : days === 0 ? "Bugün" : `${days} gün kaldı`}
      </div>
    </div>
  );
}
